import React, { FunctionComponent, useState, useRef, createRef, useCallback, useContext } from "react";
import { Grid, Typography, Theme, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { LoginFormButton } from "./form-components/login-form-button";
import { LoginFormCodeTextField } from "./form-components/login-form-code-textfield";
import { useVerifyCodeMutation } from "../../api/smartmelts-api/graphql/generated";
import { useGraphQL } from "../../api/context/graphql-context";
import { LoginContext } from "./context/login-context";
import { LOGIN_FORM_TYPE } from "../../models/login-form-type";
import { ROUTES } from "../../router/router";
import { SnackbarSeverity, useSnackbar } from "../../components/snackbar/snackbar-context";
import { getGraphqlErrorLocalized } from "../../api/errors/graphql-error-handler";
import { useHistory } from "react-router";
import { useEnterCallback } from "../../hooks/use-enter-callback";

const useStyles = makeStyles((theme: Theme) => ({
  infoSection: {
    padding: theme.spacing(2),
  },
  codeSection: {
    maxWidth: 400,
  },
}));

interface ICodeFormProps {}

export const CodeForm: FunctionComponent<ICodeFormProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { setAuthorizationHeader } = useGraphQL();
  const { setFormType, toggleShakeAnimation, initialCode } = useContext(LoginContext);
  const [code, setCode] = useState<string[]>(convertInitialCode(initialCode));
  const codeRef = useRef<any>([createRef(), createRef(), createRef(), createRef(), createRef(), createRef()]);
  const { showSnackbar } = useSnackbar();
  const history = useHistory();

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
      const clone = [...code];
      clone[index] = event.target.value;
      setCode(clone);

      const nextIndex = clone[index] === "" ? index - 1 : index + 1;

      codeRef.current[nextIndex]?.focus();
    },
    [code, setCode, codeRef],
  );

  const onKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
      const nextIndex = index - 1;
      if (event.code === "Backspace" && nextIndex !== -1 && code[index] === "") {
        codeRef.current[nextIndex]?.focus();
      }
    },
    [code],
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const pastedCode = event.clipboardData.getData("text/plain");
      const clone = [...code];
      const repeat = pastedCode.length < 6 ? pastedCode.length : 6;

      for (let i = 0; i < repeat; i++) {
        clone[i] = pastedCode[i];
      }
      setCode(clone);
      codeRef.current[repeat - 1]?.focus();
    },
    [code, setCode],
  );

  const [verifyCodeMutation, { loading }] = useVerifyCodeMutation({
    onCompleted: (data) => {
      setAuthorizationHeader(data.verifyCode.token);
      setFormType(LOGIN_FORM_TYPE.LOGIN);
      history.push(ROUTES.PORTAL.ROUTES.ONBOARDING.PATH);
    },
    onError: (error) => {
      toggleShakeAnimation();
      showSnackbar(getGraphqlErrorLocalized(t, "login", error), SnackbarSeverity.ERROR);
    },
  });

  const onVerifyCode = useCallback(() => {
    const joinedCode = code.join("");
    if (joinedCode.length === 6) {
      verifyCodeMutation({
        variables: {
          code: joinedCode,
        },
      });
    } else {
      toggleShakeAnimation();
    }
  }, [code, verifyCodeMutation, toggleShakeAnimation]);

  const onKeyDown = useEnterCallback(onVerifyCode);

  return (
    <form onSubmit={onVerifyCode}>
      <Grid container justify="center">
        <Grid item xs={12} className={classes.infoSection}>
          <Typography align="center">{t("login.code_info")}</Typography>
        </Grid>
        <Grid item container xs={12} className={classes.codeSection}>
          {code.map((codeItem, index) => (
            <Grid item xs={2} key={index}>
              <LoginFormCodeTextField
                onPaste={onPaste}
                value={codeItem}
                onKeyUp={(event) => onKeyUp(event, index)}
                onKeyDown={onKeyDown}
                onChange={(event) => onChange(event, index)}
                ref={(el) => (codeRef.current[index] = el)}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid container justify="center">
        <LoginFormButton onClick={onVerifyCode} loading={loading}>
          {t("login.log_in")}
        </LoginFormButton>
      </Grid>
    </form>
  );
};

const convertInitialCode = (initialCode?: string) => {
  if (!initialCode) {
    return ["", "", "", "", "", ""];
  }

  let codeArray = [];

  for (let i = 0; i < 6; i++) {
    codeArray[i] = initialCode[i] || "";
  }

  return codeArray;
};
